import React, { useState } from 'react';
import { Trophy, Plus, Edit, Trash2, Search, Filter, Award, Star, Crown, Shield } from 'lucide-react';

const BadgeManagement = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);

  const badges = [
    {
      id: '1',
      name: 'Gold Developer',
      type: 'gold',
      description: 'Exceptional performance in technical assessments',
      criteria: 'Score 90%+ in 3 consecutive technical tests',
      icon: Crown,
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-100',
      issuedCount: 342,
      isActive: true,
      createdDate: '2024-01-15'
    },
    {
      id: '2',
      name: 'Silver Developer',
      type: 'silver',
      description: 'Strong technical skills and consistent performance',
      criteria: 'Score 80%+ in technical assessments',
      icon: Award,
      color: 'text-gray-600',
      bgColor: 'bg-gray-100',
      issuedCount: 892,
      isActive: true,
      createdDate: '2024-01-15'
    },
    {
      id: '3',
      name: 'Communication Expert',
      type: 'skill',
      description: 'Outstanding communication skills in interviews',
      criteria: 'Score 95%+ in communication assessment',
      icon: Star,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100',
      issuedCount: 156,
      isActive: true,
      createdDate: '2024-02-01'
    },
    {
      id: '4',
      name: 'Problem Solver',
      type: 'skill',
      description: 'Exceptional problem-solving abilities',
      criteria: 'Complete 5 scenario-based tests with 85%+ score',
      icon: Shield,
      color: 'text-green-600',
      bgColor: 'bg-green-100',
      issuedCount: 234,
      isActive: true,
      createdDate: '2024-02-15'
    },
    {
      id: '5',
      name: 'Beta Tester',
      type: 'special',
      description: 'Early platform adopter and feedback provider',
      criteria: 'Joined platform in beta phase',
      icon: Trophy,
      color: 'text-purple-600',
      bgColor: 'bg-purple-100',
      issuedCount: 45,
      isActive: false,
      createdDate: '2023-12-01'
    }
  ];

  const recentIssuances = [
    {
      id: '1',
      badgeName: 'Gold Developer',
      recipientName: 'John Doe',
      recipientEmail: 'john.doe@email.com',
      issuedDate: '2025-01-14',
      issuedBy: 'Admin User'
    },
    {
      id: '2',
      badgeName: 'Communication Expert',
      recipientName: 'Sarah Johnson',
      recipientEmail: 'sarah.j@email.com',
      issuedDate: '2025-01-13',
      issuedBy: 'Admin User'
    },
    {
      id: '3',
      badgeName: 'Silver Developer',
      recipientName: 'Michael Chen',
      recipientEmail: 'michael.chen@email.com',
      issuedDate: '2025-01-12',
      issuedBy: 'HR Manager'
    }
  ];

  const getTypeBadge = (type) => {
    const colors = {
      gold: 'bg-yellow-100 text-yellow-800',
      silver: 'bg-gray-100 text-gray-800',
      skill: 'bg-blue-100 text-blue-800',
      special: 'bg-purple-100 text-purple-800'
    };
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colors[type] || colors.special}`}>
        {type.charAt(0).toUpperCase() + type.slice(1)}
      </span>
    );
  };

  const filteredBadges = badges.filter(badge => {
    const matchesSearch = badge.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         badge.description.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = filterType === 'all' || badge.type === filterType;
    
    return matchesSearch && matchesFilter;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Badge Management</h1>
          <p className="text-gray-600 mt-1">Create and manage achievement badges for users</p>
        </div>
        <button 
          onClick={() => setShowCreateModal(true)}
          className="flex items-center px-4 py-2 bg-red-800 text-white rounded-lg hover:bg-red-900 transition-colors"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Badge
        </button>
      </div>

      {/* Stats Overview */}
      <div className="grid md:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 text-center">
          <div className="text-2xl font-bold text-yellow-600">342</div>
          <div className="text-sm text-gray-600">Gold Badges</div>
        </div>
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 text-center">
          <div className="text-2xl font-bold text-gray-600">892</div>
          <div className="text-sm text-gray-600">Silver Badges</div>
        </div>
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 text-center">
          <div className="text-2xl font-bold text-blue-600">390</div>
          <div className="text-sm text-gray-600">Skill Badges</div>
        </div>
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 text-center">
          <div className="text-2xl font-bold text-purple-600">1,624</div>
          <div className="text-sm text-gray-600">Total Issued</div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
          <div className="flex-1 max-w-md">
            <div className="relative">
              <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search badges..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-red-500 focus:border-red-500"
              />
            </div>
          </div>
          
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <Filter className="w-4 h-4 text-gray-400" />
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-red-500 focus:border-red-500"
              >
                <option value="all">All Types</option>
                <option value="gold">Gold</option>
                <option value="silver">Silver</option>
                <option value="skill">Skill</option>
                <option value="special">Special</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Badges Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredBadges.map((badge) => {
          const IconComponent = badge.icon;
          return (
            <div key={badge.id} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-start justify-between mb-4">
                <div className={`w-12 h-12 ${badge.bgColor} rounded-lg flex items-center justify-center`}>
                  <IconComponent className={`w-6 h-6 ${badge.color}`} />
                </div>
                <div className="flex items-center space-x-2">
                  {getTypeBadge(badge.type)}
                  <div className={`w-3 h-3 rounded-full ${badge.isActive ? 'bg-green-500' : 'bg-gray-400'}`}></div>
                </div>
              </div>
              
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{badge.name}</h3>
              <p className="text-gray-600 text-sm mb-4">{badge.description}</p>
              
              <div className="bg-gray-50 rounded-lg p-3 mb-4">
                <h4 className="text-xs font-medium text-gray-700 mb-1">Criteria:</h4>
                <p className="text-xs text-gray-600">{badge.criteria}</p>
              </div>
              
              <div className="flex items-center justify-between text-sm">
                <div>
                  <span className="text-gray-500">Issued: </span>
                  <span className="font-medium text-gray-900">{badge.issuedCount}</span>
                </div>
                <div className="flex space-x-2">
                  <button className="text-blue-600 hover:text-blue-800 p-1">
                    <Edit className="w-4 h-4" />
                  </button>
                  <button className="text-red-600 hover:text-red-800 p-1">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Recent Issuances */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Recent Badge Issuances</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Badge
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Recipient
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Issued Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Issued By
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {recentIssuances.map((issuance) => (
                <tr key={issuance.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <Trophy className="w-5 h-5 text-yellow-600 mr-3" />
                      <div className="text-sm font-medium text-gray-900">{issuance.badgeName}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{issuance.recipientName}</div>
                      <div className="text-sm text-gray-500">{issuance.recipientEmail}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {issuance.issuedDate}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {issuance.issuedBy}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create Badge Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Create New Badge</h3>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Badge Name</label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-red-500 focus:border-red-500"
                  placeholder="Enter badge name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
                <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-red-500 focus:border-red-500">
                  <option value="gold">Gold</option>
                  <option value="silver">Silver</option>
                  <option value="skill">Skill</option>
                  <option value="special">Special</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <textarea
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-red-500 focus:border-red-500"
                  placeholder="Describe what this badge represents"
                ></textarea>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Criteria</label>
                <textarea
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-red-500 focus:border-red-500"
                  placeholder="Define the criteria for earning this badge"
                ></textarea>
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 bg-red-800 text-white rounded-lg hover:bg-red-900 transition-colors"
              >
                Create Badge
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BadgeManagement;